import { useEffect, useState } from "react";
import { toast } from "sonner";
import { errorMessage } from "../../api/client";
import { reviewApi } from "../../api/clock";
import { tsApi, type CorrectionOptions } from "../../api/timesheets";
import { Button, ErrorText, TextInput } from "../../components/ui";
import { CATEGORY_LABEL } from "./MyCorrectionItem";

const CATS = ["FORGOT_IN", "FORGOT_OUT", "WRONG_TIME", "GPS_PROBLEM", "OTHER"];
const sel = "h-10 w-full rounded-sm border border-white/15 bg-surface px-3 text-sm text-white";

export function CorrectionForm({ onSaved }: { onSaved: () => void }) {
  const [opts, setOpts] = useState<CorrectionOptions>({ sessions: [], shifts: [] });
  const [target, setTarget] = useState("");
  const [category, setCategory] = useState("");
  const [tin, setTin] = useState("");
  const [tout, setTout] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { tsApi.options().then(setOpts).catch(() => undefined); }, []);
  const submit = async () => {
    setBusy(true);
    setError(null);
    const [kind, id] = target.split(":");
    try {
      await reviewApi.requestCorrection({ [kind === "s" ? "session_id" : "assignment_id"]: id, category, requested_in_local: tin || null,
        requested_out_local: tout || null, notes: notes || null });
      toast.success("Correction request sent");
      setTin(""); setTout(""); setNotes("");
      onSaved();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const none = !opts.sessions.length && !opts.shifts.length;
  const [tk, tid] = target.split(":");
  const locked = tk === "s" ? opts.sessions.find((s) => s.session_id === tid)?.finalized : opts.shifts.find((s) => s.assignment_id === tid)?.finalized;
  return (
    <div data-testid="correction-request-form" className="space-y-3 rounded-sm border border-white/10 bg-surface p-5">
      <h2 className="font-display text-xl font-bold uppercase text-white">Request a correction</h2>
      {none ? <p data-testid="correction-no-shifts" className="text-sm text-zinc-400">You have no shifts in the last 14 days or in an open pay period.</p> : (
        <>
          <label className="block text-xs text-zinc-400">Session or shift<select value={target} onChange={(e) => setTarget(e.target.value)} className={sel} data-testid="correction-target-select">
            <option value="">Choose…</option>
            {opts.sessions.length > 0 && <optgroup label="My sessions">{opts.sessions.map((s) => <option key={s.session_id} value={`s:${s.session_id}`}>{s.label}</option>)}</optgroup>}
            <optgroup label="My shifts (session missing)">{opts.shifts.map((s) => <option key={s.assignment_id} value={`a:${s.assignment_id}`}>{s.label}</option>)}</optgroup>
          </select></label>
          <label className="block text-xs text-zinc-400">Reason (required)<select value={category} onChange={(e) => setCategory(e.target.value)} className={sel} data-testid="correction-category-select"><option value="">Choose…</option>{CATS.map((c) => <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>)}</select></label>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-xs text-zinc-400">Requested IN (venue local)<TextInput type="datetime-local" value={tin} onChange={(e) => setTin(e.target.value)} data-testid="correction-in-input" /></label>
            <label className="block text-xs text-zinc-400">Requested OUT (venue local)<TextInput type="datetime-local" value={tout} onChange={(e) => setTout(e.target.value)} data-testid="correction-out-input" /></label>
          </div>
          <label className="block text-xs text-zinc-400">Notes (optional)<textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} maxLength={1000} className="mt-1 w-full rounded-sm border border-white/15 bg-ink p-2 text-sm text-white" data-testid="correction-note" /></label>
          <ErrorText message={error} testId="correction-form-error" />
          {locked && <p data-testid="correction-period-finalized" className="text-sm text-amber-200">This pay period is finalized – contact your administrator.</p>}
          <Button onClick={submit} loading={busy} disabled={!target || !category || (!tin && !tout) || !!locked} data-testid="correction-form-submit">Send to admin</Button>
        </>
      )}
    </div>
  );
}
